import { StyleSheet, View } from "react-native";
import { useState } from "react";

import {
  AccountNotificationPreferences,
  type AccountNotificationPreferenceItem,
  type AccountNotificationPreferenceKey,
} from "../../../../components/companion/NotificationSettings/AccountNotificationPreferences";
import { CourseNudgePreferences } from "../../../../components/companion/NotificationSettings/CourseNudgePreferences";
import { radii, spacing, colors } from "../../../../styles/design-system";
import { EmailSmsPreferences } from "./components/EmailSmsPreferences";
import { ScheduleAndSnoozePreferences } from "./components/ScheduleAndSnoozePreferences";
import { useNotificationPreferencesState } from "./hooks/useNotificationPreferencesState";

const defaultAccountNotificationPreferences: AccountNotificationPreferenceItem[] = [
  {
    key: "enrollmentDeadlines",
    title: "Enrollment & Deadlines",
    description: "Registration, add/drop, graduation",
    enabled: false,
  },
  {
    key: "financial",
    title: "Financial",
    description: "FAFSA, payments, aid deadlines",
    enabled: false,
  },
];

export const NotificationSettingsContainer = () => {
  const [accountNotificationPreferences, setAccountNotificationPreferences] = useState<
    AccountNotificationPreferenceItem[]
  >(defaultAccountNotificationPreferences);
  const {
    emailEnabled,
    textMessagesEnabled,
    activeSmsPhoneNumber,
    smsConsent,
    smsPhoneNumber,
    smsCountryCode,
    isSmsPhoneVerified,
    frequency,
    maxEmailPerDay,
    maxSmsPerDay,
    preferredSendTime,
    timezone,
    snoozeAllUntil,
    courseNudgePreferences,
    courseNudgePreferencesLoading,
    animationsEnabled,
    activeSnooze,
    shouldHoldInitialRender,
    enableAnimations,
    handlePhoneVerificationSuccess,
    setSmsPhoneNumber,
    handleEmailNotificationsToggle,
    handleTextMessagesToggle,
    handleSmsConsentChange,
    handleFrequencyChange,
    handleEmailMaxPerDayChange,
    handleSmsMaxPerDayChange,
    handlePreferredSendTimeChange,
    handleTimezoneChange,
    handlePresetSnoozeOptionPress,
    handleCustomSnoozeSelect,
    handleResumeSnooze,
    handleCourseNudgePreferenceToggle,
  } = useNotificationPreferencesState();

  const handleAccountNotificationToggle = (key: AccountNotificationPreferenceKey, enabled: boolean) => {
    setAccountNotificationPreferences((previousPreferences) =>
      previousPreferences.map((preference) => (preference.key === key ? { ...preference, enabled } : preference))
    );
  };

  if (shouldHoldInitialRender) {
    return (
      <View style={styles.outerContainer}>
        <View style={styles.sectionSkeleton} />
        <View style={styles.sectionSkeleton} />
      </View>
    );
  }

  return (
    <View style={styles.outerContainer}>
      <CourseNudgePreferences
        courses={courseNudgePreferences}
        loading={courseNudgePreferencesLoading}
        onToggleCourse={handleCourseNudgePreferenceToggle}
      />

      <AccountNotificationPreferences
        items={accountNotificationPreferences}
        onToggleItem={handleAccountNotificationToggle}
      />

      <View style={styles.mutedSectionGroup}>
        <EmailSmsPreferences
          emailEnabled={emailEnabled}
          textMessagesEnabled={textMessagesEnabled}
          activeVerifiedSmsPhoneNumber={activeSmsPhoneNumber}
          smsConsent={smsConsent}
          smsPhoneNumber={smsPhoneNumber}
          smsCountryCode={smsCountryCode}
          isSmsPhoneVerified={isSmsPhoneVerified}
          onPhoneVerificationSuccess={handlePhoneVerificationSuccess}
          frequencyValue={String(frequency)}
          emailMaxPerDayValue={`${maxEmailPerDay}`}
          smsMaxPerDayValue={`${maxSmsPerDay}`}
          animationsEnabled={animationsEnabled}
          onUserInteraction={enableAnimations}
          onEmailNotificationsToggle={handleEmailNotificationsToggle}
          onTextMessagesToggle={handleTextMessagesToggle}
          onSmsConsentChange={handleSmsConsentChange}
          onFrequencyChange={handleFrequencyChange}
          onEmailMaxPerDayChange={handleEmailMaxPerDayChange}
          onSmsMaxPerDayChange={handleSmsMaxPerDayChange}
          onSmsPhoneNumberChange={setSmsPhoneNumber}
        />

        <ScheduleAndSnoozePreferences
          preferredSendTime={preferredSendTime}
          timezone={timezone}
          activeSnooze={activeSnooze}
          snoozeAllUntil={snoozeAllUntil}
          animationsEnabled={animationsEnabled}
          onUserInteraction={enableAnimations}
          onPreferredSendTimeChange={handlePreferredSendTimeChange}
          onTimezoneChange={handleTimezoneChange}
          onPresetSnoozeSelect={handlePresetSnoozeOptionPress}
          onCustomSnoozeSelect={handleCustomSnoozeSelect}
          onResumeSnooze={handleResumeSnooze}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    gap: spacing.sm,
  },
  mutedSectionGroup: {
    gap: spacing.sm,
  },
  sectionSkeleton: {
    height: 320,
    borderRadius: radii.lg,
    backgroundColor: colors.grey[100],
  },
});
